import {Dialog, Loading, TransitionDiv} from '@startlibs/components'
import {useToggle} from '@startlibs/core'
import {getColor, getFetcher, media, stopPropagation, wrapLazy} from '@startlibs/utils';
import React, {Suspense} from 'react'
import styled from 'styled-components'
import {FullPageDroppable} from './FullPageDroppable'
import {HeaderInfo} from './page/HeaderInfo'
import {PurviewFooter} from '../components/PurviewFooter';
import {RecordList} from './page/RecordList'
import {RecordsManager, UploaderAction} from './lib/service'
import {UploadButtonBox} from './page/UploadButtonBox'
import {UploadLogDialog} from './UploadLogDialog'
import _ from 'lodash/fp'
import {createAutoRefreshableJwtFromLazy} from './lib/utils/autoRefershableJwtFromLazy'
import {jwtGetFetcher, jwtPostFetcher} from '../utils/authFetch'
import {UploaderOrigin} from '../config/UploaderOrigin'
import {UnauthorizedUploadError} from './UnauthorizedUploadError'
import {touchClick} from './panel/utils'
import {PanelButton, WidgetHeader} from './panel/PanelStyles'

const UploaderPageStyled = styled.div`
  margin: auto;
  max-width: 74rem;
  padding: 7rem 2rem 90px;
  min-height: 100vh;
  position: relative;
  ${media.mobile`
    padding: 5rem 1rem 90px;
  `}
`
const Navbar = styled.div`
  background: ${getColor('main')};
  height: 4rem;
  color: white;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  line-height: 4rem;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  div {
    padding: 0 1.25rem;
  }
`
const Card = styled.div`
  padding: 2rem 2.5rem 2.5rem;
  background: white;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  margin: 0 auto;
  ${media.mobile`
    padding: 1.5rem;
  `}
`
const ProcessingFilesBox = styled.div`
  padding: 0.75rem 1.5rem 0.75rem 1.5rem;
  display: flex;
  justify-content: center;
  font-weight: 600;
  min-height: 3rem;
  background: #f0f6f8;
  border-radius: 6px;
  position: relative;
  border: 1px solid #cddee3;
  margin-top: 1rem;
  div {
    margin-right: 0.75rem;
  }
`
const UploaderPurviewFooter = styled(PurviewFooter)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`

const formatUnauthorizedError = (e) => Promise.reject(
  _.get([1,'status'],e) === 401
  ? {render: <UnauthorizedUploadError />}
  : e
)

const lazyAuth = wrapLazy(() => jwtPostFetcher(new URLSearchParams(window.location.search).get("auth"))(UploaderOrigin() + "/auth")
  .catch(formatUnauthorizedError))

const lazyJwt = wrapLazy((currentJwt) =>
   jwtGetFetcher(currentJwt)(UploaderOrigin() + "/refresh").then(_.get('jwt')).catch(formatUnauthorizedError)
)

const getJwt = createAutoRefreshableJwtFromLazy(lazyJwt, 0.2)

export const UploaderPage = () => {

  const uploadLogDialog = useToggle()
  const error = useToggle()

  const auth = lazyAuth.read()
  React.useEffect(() => {
    lazyJwt.use(auth.jwt).promise().catch(error.open)
  },[])

  if (error.isOpen) {
    return <div>Error</div>
  }

  return <RecordsManager getJwt={getJwt} resolveJwt={lazyJwt.promise}>{(records, data, doAction) => <UploaderPageStyled>
    <Navbar>
      <div>{auth.institutionName}</div>
      <div>Purview</div>
    </Navbar>
    <Card>
      <HeaderInfo records={records} data={data} openUploadLog={uploadLogDialog.open}/>
      <UploadButtonBox fileEventHandler={doAction(UploaderAction.UploadFromEvent)}/>
      <FullPageDroppable fileEventHandler={doAction(UploaderAction.UploadFromEvent)}/>
      <UploaderPageContent data={data} doAction={doAction} records={records} uploadLogDialog={uploadLogDialog} />
      {
        uploadLogDialog.isOpen &&
        <UploadLogDialog closeDialog={uploadLogDialog.close} filter={uploadLogDialog.isOpen.filter}/>
      }
    </Card>
    <UploaderPurviewFooter/>
  </UploaderPageStyled>}</RecordsManager>
}

const UploaderPageContent = ({data, canRemove, doAction,records,uploadLogDialog}) =>
  <>
    <TransitionDiv block>{data.isProcessing > 0 &&
    <ProcessingFilesBox><Loading size={18} borderWidth={3}/>
      <div>Processing files...</div>
    </ProcessingFilesBox>
    }</TransitionDiv>
    <RecordList canRemove={canRemove} uploadLogDialog={uploadLogDialog} doAction={doAction} records={records} data={data}/>
  </>

const StudyUploaderDialog = styled(Dialog)`
    max-width: 80rem;
    min-height: 80vh;
`

export const UploaderDialog = ({closeDialog, canRemove = true,data,doAction,records,uploadLogDialog}) => {
  const isUploading = records.find(({fileProgress}) => fileProgress.total - fileProgress.uploaded - fileProgress.failed)

  return <StudyUploaderDialog
    closeDialog={closeDialog}
    skipCloseIcon
  >
    <WidgetHeader isOnDialog>
      {isUploading && <Loading white/>}
      <div css="margin-right:auto;">
        {
          isUploading
            ? <h4>Uploading studies</h4>
            : <h4>All studies uploaded</h4>
        }
        <a onClick={stopPropagation(uploadLogDialog.open)}>Upload log</a>
      </div>
      <PanelButton icon="x" onClick={closeDialog}/>
    </WidgetHeader>
    <UploaderPageContent
      canRemove={canRemove}
      data={data}
      doAction={doAction}
      records={records}
      uploadLogDialog={uploadLogDialog}
    />
  </StudyUploaderDialog>
}